import React, { useEffect, useMemo, useState } from 'react';
import { Container, Collapse, Form } from 'react-bootstrap';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import api, { DashboardRecord, Company, UserInfo } from '../services/api';
import CompanySettings from './CompanySettings';
import UpgradeModal from './UpgradeModal';
import AdminUserSelector from './AdminUserSelector';
import { useAuth } from '../contexts/AuthContext';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Validate and sanitize hex color to prevent CSS injection
const sanitizeColor = (color: string | undefined, fallback: string = '#007bff'): string => {
  if (!color) return fallback;
  // Only allow valid hex colors
  const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexColorRegex.test(color) ? color : fallback;
};

const PROVIDER_LABELS: Record<string, string> = { google: 'Google', microsoft: 'Microsoft' };

const AdminDashboard: React.FC = () => {
  const [caseStudies, setCaseStudies] = useState<DashboardRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [companySettingsOpen, setCompanySettingsOpen] = useState(false);
  const [embedCodeOpen, setEmbedCodeOpen] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [copiedShareLink, setCopiedShareLink] = useState(false);
  const [copiedRecordId, setCopiedRecordId] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [signInMethodsOpen, setSignInMethodsOpen] = useState(false);
  // /me is fetched here rather than threaded through AuthContext: the context holds tier,
  // isAdmin and the cached limits, not arbitrary /me fields, and mirroring two more into
  // localStorage would buy nothing.
  const [me, setMe] = useState<UserInfo | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkStarting, setLinkStarting] = useState(false);
  const { userId } = useParams<{ userId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();

  useEffect(() => {
    loadData();
  }, [userId]);

  // Handle upgrade success/cancelled from Stripe redirect
  useEffect(() => {
    const upgrade = searchParams.get('upgrade');
    if (upgrade === 'success') {
      setSuccessMessage('Welcome to Pro! Your account has been upgraded.');
      auth.refreshUserInfo(); // Refresh to get new tier
      searchParams.delete('upgrade');
      setSearchParams(searchParams);
    } else if (upgrade === 'cancelled') {
      setError('Upgrade cancelled. You can try again anytime.');
      searchParams.delete('upgrade');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  // Sign-in methods describe the *caller's own* account, so don't load them while impersonating
  // (the backend refuses to link in that mode) or while viewing another tenant's dashboard.
  useEffect(() => {
    if (auth.isImpersonating) {
      setMe(null);
      return;
    }
    let cancelled = false;
    api.getUserInfo()
      .then((info) => { if (!cancelled) setMe(info); })
      .catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, [auth.isImpersonating, userId]);

  const handleConnectProvider = async (provider: 'google' | 'microsoft') => {
    setLinkError(null);
    setLinkStarting(true);
    try {
      const { token } = await api.startOAuthLink();
      // Full navigation, not axios: the OAuth handshake is a browser redirect chain.
      window.location.href = `${API_BASE_URL}/auth/${provider}?link=${encodeURIComponent(token)}`;
    } catch (err) {
      setLinkStarting(false);
      setLinkError(`Could not start connecting ${PROVIDER_LABELS[provider]}. Please try again.`);
    }
  };

  const loadData = async () => {
    try {
      const [caseStudiesData, companyData] = await Promise.all([
        api.getDashboardRecords(userId),
        api.getCompany(userId)
      ]);
      setCaseStudies(caseStudiesData);
      setCompany(companyData);
    } catch (err) {
      setLoadFailed(true);
      setError('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCaseStudies = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return caseStudies;
    return caseStudies.filter(
      (cs) =>
        cs.projectName.toLowerCase().includes(q) ||
        (cs.country || '').toLowerCase().includes(q)
    );
  }, [caseStudies, searchQuery]);

  const handleCompanyUpdate = () => {
    // Trigger navigation update by reloading the page
    window.location.reload();
  };

  const handleShareRecord = async (id: string) => {
    const base = window.location.origin;
    const url = userId
      ? `${base}/${userId}/case-study/${id}`
      : `${base}/case-study/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedRecordId(id);
      setTimeout(() => {
        setCopiedRecordId((curr) => (curr === id ? null : curr));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/billing/create-portal-session`);
      window.location.href = response.data.url;
    } catch (err) {
      setError('Failed to open subscription management. Please try again.');
    }
  };

  // Generate embed code for the map
  const getEmbedCode = () => {
    const clientId = userId || auth.userId || 'YOUR_CLIENT_ID';
    const embedUrl = `${window.location.origin}/embed/${clientId}`;
    return `<iframe
  src="${embedUrl}"
  width="100%"
  height="600"
  style="border: none; border-radius: 8px;"
  title="Project Map"
></iframe>`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getEmbedCode());
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const getShareableLink = () => {
    const clientId = userId || auth.userId || '';
    return `${window.location.origin}/${clientId}`;
  };

  const copyShareableLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareableLink());
      setCopiedShareLink(true);
      setTimeout(() => setCopiedShareLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="admin-loading__spinner" />
        </div>
      </div>
    );
  }

  const accentColor = sanitizeColor(company?.primaryColor);

  return (
    <div className="admin-page">
      <Container>
        {/* Help contact — visible at the top so support is always one click away. */}
        <div
          style={{
            fontSize: '0.875rem',
            color: 'var(--admin-text-light, #64748b)',
            margin: '0.75rem 0 1.25rem',
            textAlign: 'right',
          }}
        >
          Need help? Email{' '}
          <a href="mailto:info@mapofprojects.com">info@mapofprojects.com</a>
        </div>

        {/* Admin User Management (only visible to admins) */}
        <AdminUserSelector />

        {/* Company Settings Section */}
        <div className="admin-card admin-card--accent" style={{ borderTopColor: accentColor }}>
          <div className="card-header">
            <button
              className="admin-collapse-toggle"
              onClick={() => setCompanySettingsOpen(!companySettingsOpen)}
              aria-controls="company-settings-collapse"
              aria-expanded={companySettingsOpen}
            >
              <h5>Personalise Your Map</h5>
              <span className={`admin-collapse-toggle__icon${companySettingsOpen ? ' admin-collapse-toggle__icon--open' : ''}`}>&#9654;</span>
            </button>
          </div>
          <Collapse in={companySettingsOpen}>
            <div id="company-settings-collapse">
              <div className="card-body" style={{ padding: 0 }}>
                <CompanySettings onUpdate={handleCompanyUpdate} />
              </div>
            </div>
          </Collapse>
        </div>

        {/* Share & Embed Section (merged: shareable link + website embed code) */}
        <div className="admin-card admin-card--accent" style={{ borderTopColor: accentColor }}>
          <div className="card-header">
            <button
              className="admin-collapse-toggle"
              onClick={() => setEmbedCodeOpen(!embedCodeOpen)}
              aria-controls="embed-code-collapse"
              aria-expanded={embedCodeOpen}
            >
              <h5>Share Your Map</h5>
              <span className={`admin-collapse-toggle__icon${embedCodeOpen ? ' admin-collapse-toggle__icon--open' : ''}`}>&#9654;</span>
            </button>
          </div>
          <Collapse in={embedCodeOpen}>
            <div id="embed-code-collapse">
              <div className="card-body">
                {/* Part 1 — public map link */}
                <div style={{ fontSize: '0.9rem', color: 'var(--admin-text-light)', marginBottom: '0.75rem' }}>
                  Copy your public map link.
                </div>
                <div className="d-flex gap-2" style={{ alignItems: 'stretch', flexWrap: 'wrap' }}>
                  <Form.Control
                    readOnly
                    value={getShareableLink()}
                    onFocus={(e) => e.target.select()}
                    style={{ flex: '1 1 260px', minWidth: 0 }}
                  />
                  <button
                    className={`admin-btn ${copiedShareLink ? 'admin-btn--copy-success' : 'admin-btn--primary'}`}
                    onClick={copyShareableLink}
                  >
                    {copiedShareLink ? 'Copied!' : 'Copy link'}
                  </button>
                  <button
                    className="admin-btn admin-btn--outline"
                    onClick={() => window.open(`${getShareableLink()}?view=public`, '_blank', 'noopener')}
                  >
                    Preview as visitor ↗
                  </button>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-light)', marginTop: '0.625rem' }}>
                  Only the map is public — your admin controls and login stay private to you.
                </div>

                <hr style={{ margin: '1.75rem 0', borderTop: '1px solid var(--admin-border, rgba(15,23,42,0.08))', opacity: 1 }} />

                {/* Part 2 — website embed */}
                <div className="admin-alert admin-alert--info" style={{ marginBottom: '1.25rem' }}>
                  Use the code below to embed your project map on any website.
                </div>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--admin-text-light)' }}>Embed Code</Form.Label>
                  <Form.Control
                    as="textarea"
                    readOnly
                    rows={5}
                    value={getEmbedCode()}
                    className="admin-embed-textarea"
                  />
                </Form.Group>

                <div className="d-flex gap-2">
                  <button
                    className={`admin-btn ${copiedToClipboard ? 'admin-btn--copy-success' : 'admin-btn--primary'}`}
                    onClick={copyToClipboard}
                  >
                    {copiedToClipboard ? "Copied!" : "Copy to Clipboard"}
                  </button>
                  <button
                    className="admin-btn admin-btn--outline"
                    onClick={() => window.open(`/embed/${userId || auth.userId}`, '_blank')}
                  >
                    Preview Embedded (without nav bar)
                  </button>
                </div>

                <div className="admin-embed-guide">
                  <h6>Quick Integration Guide</h6>
                  <ol>
                    <li>Copy the embed code above</li>
                    <li>Paste it into your website's HTML where you want the map to appear</li>
                    <li>Adjust the width and height attributes as needed</li>
                    <li>The map will automatically display all your case studies</li>
                  </ol>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        {/* Sign-in Methods — hidden while impersonating (POST /api/auth/link/start 403s in that
            mode) and when an admin is looking at someone else's dashboard, since the flow always
            acts on the signed-in account. */}
        {!auth.isImpersonating && me && (!userId || userId === me.userId) && (
          <div className="admin-card admin-card--accent" style={{ borderTopColor: accentColor }}>
            <div className="card-header">
              <button
                className="admin-collapse-toggle"
                onClick={() => setSignInMethodsOpen(!signInMethodsOpen)}
                aria-controls="sign-in-methods-collapse"
                aria-expanded={signInMethodsOpen}
              >
                <h5>Sign-in Methods</h5>
                <span className={`admin-collapse-toggle__icon${signInMethodsOpen ? ' admin-collapse-toggle__icon--open' : ''}`}>&#9654;</span>
              </button>
            </div>
            <Collapse in={signInMethodsOpen}>
              <div id="sign-in-methods-collapse">
                <div className="card-body">
                  {linkError && (
                    <div className="admin-alert admin-alert--danger" style={{ marginBottom: '1rem' }}>
                      {linkError}
                    </div>
                  )}

                  <div style={{ fontSize: '0.9rem', color: 'var(--admin-text-light)', marginBottom: '0.75rem' }}>
                    How you can sign in to this account today.
                  </div>
                  <ul style={{ marginBottom: '1.25rem', paddingLeft: '1.25rem' }}>
                    <li>
                      <strong>Email and password:</strong>{' '}
                      {me.hasPassword ? 'set up' : 'not set up'}
                    </li>
                    <li>
                      <strong>Connected account:</strong>{' '}
                      {me.linkedProvider ? (PROVIDER_LABELS[me.linkedProvider] || me.linkedProvider) : 'none'}
                    </li>
                  </ul>

                  {me.linkedProvider ? (
                    <div className="admin-alert admin-alert--info">
                      Your {PROVIDER_LABELS[me.linkedProvider] || me.linkedProvider} account is connected — use
                      &ldquo;Continue with {PROVIDER_LABELS[me.linkedProvider] || me.linkedProvider}&rdquo; on the
                      login page. An account can have only one connected provider, so Google and Microsoft cannot
                      both be connected.
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.9rem', color: 'var(--admin-text-light)', marginBottom: '0.75rem' }}>
                        Connect Google or Microsoft to sign in with one click. You stay in this same account with
                        the same records, and your password keeps working.
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          className="admin-btn admin-btn--outline"
                          disabled={linkStarting}
                          onClick={() => handleConnectProvider('google')}
                        >
                          Connect Google
                        </button>
                        <button
                          className="admin-btn admin-btn--outline"
                          disabled={linkStarting}
                          onClick={() => handleConnectProvider('microsoft')}
                        >
                          Connect Microsoft
                        </button>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-light)', marginTop: '0.625rem' }}>
                        Only one provider can be connected, and it cannot be swapped afterwards — pick the one you
                        actually sign in with.
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Collapse>
          </div>
        )}

        {/* Free-tier upsell — sits between Share Your Map and Your Records so the user
            encounters it mid-sharing-flow, when "remove the badge" is most concrete. */}
        {auth.tier === 'free' && (
          <div className="admin-card admin-upsell">
            <div className="admin-upsell__body">
              <div className="admin-upsell__copy">
                <div className="admin-upsell__eyebrow">Upgrade to Pro</div>
                <h3 className="admin-upsell__title">Take the free badge off your maps</h3>
                <p className="admin-upsell__lede">
                  Free maps and shared pages show a &ldquo;Made with Map of Projects&rdquo; badge.
                  Pro removes it and unlocks the rest.
                </p>
                <ul className="admin-upsell__bullets">
                  <li>Remove &ldquo;Made with Map of Projects&rdquo; badges from embeds and shared pages</li>
                  <li>Up to 800 entries and 10 photos per entry</li>
                  <li>Advanced customisation: your logo, colours, and marker styling</li>
                  <li>And more</li>
                </ul>
              </div>
              <div className="admin-upsell__cta">
                <button
                  className="admin-btn admin-btn--success admin-upsell__button"
                  onClick={() => setShowUpgradeModal(true)}
                >
                  Upgrade to Pro
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Records Section */}
        <div className="admin-card admin-card--accent" style={{ borderTopColor: accentColor }}>
          <div className="card-header">
            <div className="admin-cs-header">
              <div>
                <h2 className="admin-cs-header__title">
                  Your Records
                  <span className="admin-cs-header__count"> ({caseStudies.length}
                  {auth.tier === 'free' && `/${auth.tierLimits.maxCaseStudies}`})</span>
                  {auth.tier === 'free' && <span className="admin-cs-header__badge admin-cs-header__badge--free">Free Tier</span>}
                  {auth.tier === 'pro' && <span className="admin-cs-header__badge admin-cs-header__badge--pro">Pro</span>}
                </h2>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {auth.tier === 'pro' && (
                  <button className="admin-btn admin-btn--outline admin-btn--sm" onClick={handleManageSubscription}>
                    Manage Subscription
                  </button>
                )}
                {caseStudies.length < auth.tierLimits.maxCaseStudies && (
                  <Link to={userId ? `/${userId}/admin/case-study/new` : "/admin/case-study/new"}>
                    <button className="admin-btn admin-btn--primary">Add New Record</button>
                  </Link>
                )}
                {auth.tier === 'free' && (
                  <button className="admin-btn admin-btn--success" onClick={() => setShowUpgradeModal(true)}>
                    Upgrade to Pro
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="card-body">
            {successMessage && (
              <div className="admin-alert admin-alert--success" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {successMessage}
                <button onClick={() => setSuccessMessage(null)} style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: 'inherit', padding: '0 0.25rem' }}>&times;</button>
              </div>
            )}
            {error && (
              <div className="admin-alert admin-alert--danger" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {error}
                <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: 'inherit', padding: '0 0.25rem' }}>&times;</button>
              </div>
            )}
            {caseStudies.length === 0 && !loadFailed ? (
              <div className="admin-empty">
                <div className="admin-empty__text">Nothing on the map. Create your first one!</div>
                <Link to={userId ? `/${userId}/admin/case-study/new` : "/admin/case-study/new"}>
                  <button className="admin-btn admin-btn--primary">Start Populating the Map</button>
                </Link>
              </div>
            ) : caseStudies.length === 0 && loadFailed ? null : (
              <>
                <div className="d-flex align-items-center gap-3 flex-wrap" style={{ marginBottom: '0.75rem' }}>
                  <Form.Control
                    type="search"
                    placeholder="Search by project name or country..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ maxWidth: '360px' }}
                  />
                  {searchQuery && (
                    <span style={{ fontSize: '0.85rem', color: 'var(--admin-text-light)' }}>
                      Showing {filteredCaseStudies.length} of {caseStudies.length}
                    </span>
                  )}
                </div>
                {filteredCaseStudies.length === 0 ? (
                  <div className="admin-empty">
                    <div className="admin-empty__text">No records match your search.</div>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Project Name</th>
                          <th className="d-none d-md-table-cell">Country</th>
                          <th className="d-none d-md-table-cell">Photos</th>
                          <th className="d-none d-md-table-cell">Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCaseStudies.map((caseStudy) => (
                          <tr key={caseStudy._id}>
                            <td style={{ fontWeight: 600, color: 'var(--admin-dark)' }}>
                              {caseStudy.hideFromMap && (
                                <span className="badge bg-secondary me-2" style={{ fontWeight: 500 }}>
                                  Hidden from Map
                                </span>
                              )}
                              {caseStudy.approximateLocation && (
                                <span className="badge bg-warning text-dark me-2" style={{ fontWeight: 500 }}>
                                  Approximate Location
                                </span>
                              )}
                              {caseStudy.projectName}
                            </td>
                            <td className="d-none d-md-table-cell">{caseStudy.country}</td>
                            <td className="d-none d-md-table-cell">{caseStudy.photoCount}</td>
                            <td className="d-none d-md-table-cell">{new Date(caseStudy.createdAt).toLocaleDateString()}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <Link to={userId ? `/${userId}/case-study/${caseStudy._id}` : `/case-study/${caseStudy._id}`}>
                                  <button className="admin-btn admin-btn--view admin-btn--sm">View</button>
                                </Link>
                                <Link to={userId ? `/${userId}/admin/case-study/edit/${caseStudy._id}` : `/admin/case-study/edit/${caseStudy._id}`}>
                                  <button className="admin-btn admin-btn--edit admin-btn--sm">Edit</button>
                                </Link>
                                <button
                                  className={`admin-btn admin-btn--sm ${copiedRecordId === caseStudy._id ? 'admin-btn--copy-success' : 'admin-btn--view'}`}
                                  onClick={() => handleShareRecord(caseStudy._id)}
                                  title="Copy public link to clipboard"
                                >
                                  {copiedRecordId === caseStudy._id ? 'Copied!' : 'Share'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Upgrade Modal */}
        <UpgradeModal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} />
      </Container>
    </div>
  );
};

export default AdminDashboard;